export function normalizePrice(value) {
    const number = Number(value) || 0
    return Number(number.toFixed(2))
}

export function getModuleExternalId(module) {
    return module.external_id || (module.module && module.module.external_id)
}

export function getModuleTotalAmount(module) {
    const baseAmount = module.base_amount != null ? module.base_amount : 0

    if (module.price_type === 'per_unit') {
        const add =
            module.add_amount != null
                ? Number(module.add_amount)
                : Number(module.amount != null ? module.amount : 0)
        return add
    }

    if (module.add_amount != null) {
        return baseAmount + Number(module.add_amount)
    }

    const totalAmount = Number(module.amount != null ? module.amount : 0)

    if (totalAmount && baseAmount && totalAmount >= baseAmount) {
        return totalAmount
    }

    return totalAmount || baseAmount
}

export function buildModulesPayload({
    fixedModules = [],
    optionalAddOnSelected = [],
}) {
    const modules = []

    // Helper function para extrair dados de módulos FIXOS
    // Preço vai como 0 pois o custo dos módulos fixos já está embutido no planPackage.price
    const extractFixedModuleData = module => {
        const externalId = getModuleExternalId(module)

        const amount = module.amount || 0
        if (!amount) return null

        return {
            id: module.module.external_id,
            external_id: externalId,
            amount,
            price: 0,
            over_price: 0,
        }
    }

    const extractAddonModuleData = module => {
        const moduleId =
            (module.module && module.module.external_id) || module.external_id
        if (!moduleId) return null

        const externalId = getModuleExternalId(module)

        const amount =
            module.add_amount != null && module.add_amount > 0
                ? module.add_amount
                : module.amount || 0

        if (!amount) return null

        return {
            id: moduleId,
            external_id: externalId,
            amount,
            price: normalizePrice(module.price || 0),
            over_price: 0,
        }
    }

    // 1. Adicionar TODOS os módulos fixos (exibidos no summary)
    fixedModules.forEach(fixedModule => {
        const moduleData = extractFixedModuleData(fixedModule)
        if (moduleData) {
            modules.push(moduleData)
        }
    })

    optionalAddOnSelected.forEach(optionalModule => {
        const moduleData = extractAddonModuleData(optionalModule)
        if (!moduleData) return

        const existingIndex = modules.findIndex(m => m.id === moduleData.id)
        if (existingIndex !== -1) {
            modules[existingIndex].amount += moduleData.amount
            return
        }

        modules.push(moduleData)
    })

    return modules
}

/**
 * Normaliza informações comuns de um módulo
 */
export function normalizeModuleCommon(module) {
    if (!module) return null

    const moduleId = (module.module && module.module.id) || module.module_id

    const baseAmount =
        module.base_amount != null
            ? module.base_amount
            : module.included != null
            ? module.included
            : 0

    const moduleInfo = module.module
        ? { ...module.module }
        : {
              id: moduleId,
              name: module.name,
              vindi_id: module.external_id,
              external_id: module.external_id,
              third_party: module.third_party || false,
          }

    const priceType = module.price_type || 'flat'

    return {
        moduleId,
        baseAmount,
        moduleInfo,
        priceType,
    }
}

/**
 * Formata e enriquece um módulo com informações padronizadas
 */
export function enhanceModule(module, extra = {}) {
    const common = normalizeModuleCommon(module)
    if (!common) return {}

    const { moduleId, baseAmount, moduleInfo, priceType } = common

    const amount =
        module.amount != null
            ? module.amount
            : priceType === 'per_unit'
            ? 0
            : baseAmount

    const addAmount =
        module.add_amount != null
            ? module.add_amount
            : priceType === 'per_unit'
            ? Number(module.amount) || 0
            : module.selected
            ? 1
            : 0

    const checked =
        module.checked != null
            ? module.checked
            : module.selected != null
            ? module.selected
            : false

    return {
        ...module,
        ...extra,
        module_id: moduleId,
        module: moduleInfo,
        price_type: priceType,
        amount,
        base_amount: baseAmount,
        add_amount: addAmount,
        checked,
    }
}

/**
 * Formata múltiplos módulos
 */
export function formatModules(modules, extendFn = () => ({})) {
    if (!Array.isArray(modules)) return []

    return modules.map(module => enhanceModule(module, extendFn(module)))
}

/**
 * Formata um pacote de plano
 */
export function formatPlanPackage(planPackage) {
    if (!planPackage) return {}

    const attributes = planPackage.attributes
        ? { ...planPackage.attributes }
        : { ...planPackage }

    const modules = formatModules(attributes.modules || [], () => ({
        origin: 'plan',
    }))

    return {
        id: planPackage.id || attributes.id,
        ...attributes,
        modules,
    }
}

/**
 * Formata múltiplos pacotes de plano
 */
export function formatPlanPackages(planPackagesResource) {
    if (!planPackagesResource) return []

    const mapper = planPackage => formatPlanPackage(planPackage)

    if (Array.isArray(planPackagesResource)) {
        return planPackagesResource.map(mapper)
    }

    if (Array.isArray(planPackagesResource.data)) {
        return planPackagesResource.data.map(mapper)
    }

    return []
}

/**
 * Prepara um módulo para ser usado na proposta
 */
export function prepareModuleForProposal(module) {
    const common = normalizeModuleCommon(module)
    if (!common) return null

    const { moduleId, baseAmount, moduleInfo, priceType } = common

    let addAmount
    let checked

    if (priceType === 'per_unit') {
        addAmount = Number(module.amount) || 0
        checked = addAmount > 0
    } else {
        addAmount =
            module.add_amount != null
                ? module.add_amount
                : module.selected
                ? 1
                : 0
        checked = module.selected != null ? module.selected : addAmount > 0
    }

    return {
        ...module,
        module_id: moduleId,
        module: moduleInfo,
        price_type: priceType,
        price: module.price,
        amount: baseAmount,
        add_amount: addAmount,
        checked,
    }
}
